import React, { useState, useMemo, useRef, useEffect } from 'react';
import { User, Bot, MessageSquare, Folder, Mail, Users, Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { cn } from '../lib/utils';

export interface MentionOption {
  id: string;
  type: 'person' | 'source';
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

interface MentionMenuProps {
  onSelect: (option: MentionOption) => void;
  children?: React.ReactNode;
  position?: { top: number; left: number } | null;
  onClose?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

// Mock data for people/bots
const peopleOptions: MentionOption[] = [
  { id: 'raza', type: 'person', label: 'Raza', description: "Raza's Assistant", icon: <Bot className="h-4 w-4" /> },
  { id: 'jack', type: 'person', label: 'Jack', description: "Jack's Assistant", icon: <Bot className="h-4 w-4" /> },
  { id: 'sajid', type: 'person', label: 'Sajid', description: "Sajid's Assistant", icon: <Bot className="h-4 w-4" /> },
  { id: 'umer', type: 'person', label: 'Umer', description: "Umer's Assistant", icon: <Bot className="h-4 w-4" /> },
];

// Mock data for sources
const sourceOptions: MentionOption[] = [
  { id: 'teams', type: 'source', label: 'Teams', description: 'Microsoft Teams channels and chats', icon: <MessageSquare className="h-4 w-4" /> },
  { id: 'sharepoint', type: 'source', label: 'SharePoint', description: 'SharePoint sites and documents', icon: <Folder className="h-4 w-4" /> },
  { id: 'onedrive', type: 'source', label: 'OneDrive', description: 'OneDrive files and folders', icon: <Folder className="h-4 w-4" /> },
  { id: 'email', type: 'source', label: 'Email', description: 'Email messages and calendar', icon: <Mail className="h-4 w-4" /> },
];

export const MentionMenu: React.FC<MentionMenuProps> = ({
  onSelect,
  children,
  position,
  onClose,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'people' | 'sources'>('all');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const filteredOptions = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    let filtered: MentionOption[] = [];

    if (selectedCategory === 'all' || selectedCategory === 'people') {
      filtered = [...filtered, ...peopleOptions];
    }
    if (selectedCategory === 'all' || selectedCategory === 'sources') {
      filtered = [...filtered, ...sourceOptions];
    }

    if (query) {
      filtered = filtered.filter(option =>
        option.label.toLowerCase().includes(query) ||
        option.description?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [searchQuery, selectedCategory]);

  const handleSelect = (option: MentionOption) => {
    onSelect(option);
    setOpen(false);
    setSearchQuery('');
    if (onClose) onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      if (onClose) onClose();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {children && (
        <PopoverTrigger asChild>
          {children}
        </PopoverTrigger>
      )}
      <PopoverContent
        className="w-80 p-0"
        align="start"
        side="top"
        sideOffset={8}
        onKeyDown={handleKeyDown}
      >
        <div className="flex flex-col">
          {/* Search Input */}
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={inputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search people or sources..."
                className="pl-8 h-9"
              />
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex gap-1 px-3 pt-2 border-b border-border">
            <button
              onClick={() => setSelectedCategory('all')}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                selectedCategory === 'all'
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted"
              )}
            >
              All
            </button>
            <button
              onClick={() => setSelectedCategory('people')}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5",
                selectedCategory === 'people'
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted"
              )}
            >
              <Users className="h-3.5 w-3.5" />
              People
            </button>
            <button
              onClick={() => setSelectedCategory('sources')}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5",
                selectedCategory === 'sources'
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted"
              )}
            >
              <Folder className="h-3.5 w-3.5" />
              Sources
            </button>
          </div>

          {/* Options List */}
          <ScrollArea className="max-h-[300px]">
            <div className="p-2">
              {filteredOptions.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No results found
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleSelect(option)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 rounded-md text-left",
                        "hover:bg-accent hover:text-accent-foreground",
                        "transition-colors cursor-pointer"
                      )}
                    >
                      <div className="flex-shrink-0">
                        {option.icon || (
                          option.type === 'person' ? (
                            <User className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Folder className="h-4 w-4 text-muted-foreground" />
                          )
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground">
                          {option.type === 'person' ? `@${option.label}` : option.label}
                        </div>
                        {option.description && (
                          <div className="text-xs text-muted-foreground truncate">
                            {option.description}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
};

